// 告警阈值表单的草稿、换算与展示（决策 D190、D191，docs/04 第 2 节）。作用对象只在新建时填，编辑只改比较方式、数值与备注。
import type { Errors } from "@/app/useDraft";
import { formatMoneyInput, parseMoney, validMcc, validMnc } from "@/app/form";
import type { AlertMode, AlertRule, AlertThreshold, AlertThresholdInput } from "@/app/master";

/** 每条规则的作用对象与可用的比较方式。 */
export const ALERT_RULES: Record<AlertRule, { label: string; owner: "channel" | "account"; operator: boolean; baseline: boolean }> = {
  delivery_rate: { label: "通道送达率", owner: "channel", operator: true, baseline: true },
  channel_consecutive_failures: { label: "通道连续失败", owner: "channel", operator: true, baseline: false },
  account_delivery_rate: { label: "账号送达率", owner: "account", operator: true, baseline: true },
  account_consecutive_failures: { label: "账号连续失败", owner: "account", operator: true, baseline: false },
  channel_queue_wait: { label: "通道排队时长", owner: "channel", operator: false, baseline: false },
  balance_low: { label: "余额告警线", owner: "account", operator: false, baseline: false },
};

/** 数值是正整数的规则：排队秒数与连续失败次数（D191）。 */
const isCount = (rule: AlertRule) => rule === "channel_queue_wait" || rule === "channel_consecutive_failures" || rule === "account_consecutive_failures";

/** 规则在页面上的顺序。 */
export const RULE_ORDER = Object.keys(ALERT_RULES) as AlertRule[];

export interface AlertThresholdDraft { rule: AlertRule | ""; owner_id: string; mcc: string; mnc: string; mode: AlertMode; value: string; note: string }

/** 设为不告警时仍要存一个合法的数值（排队时长与连续失败要求大于 0），取这条规则的默认值。 */
const OFF_VALUE: Record<AlertRule, number> = {
  delivery_rate: 0, account_delivery_rate: 0, channel_queue_wait: 120, balance_low: 0, channel_consecutive_failures: 20, account_consecutive_failures: 50,
};

const isRate = (rule: AlertRule | "") => rule === "delivery_rate" || rule === "account_delivery_rate";

export function alertThresholdDraft(row?: AlertThreshold): AlertThresholdDraft {
  if (!row) return { rule: "", owner_id: "", mcc: "", mnc: "", mode: "absolute", value: "", note: "" };
  return {
    rule: row.rule, owner_id: String(row.channel_id ?? row.account_id ?? ""), mcc: row.mcc ?? "", mnc: row.mnc ?? "", mode: row.mode,
    value: row.mode === "off" ? "" : valueInput(row.rule, row.value), note: row.note ?? "",
  };
}

/** 存储值转输入框文本：送达率万分比转百分数，余额微单位转金额，排队秒数与连续失败次数原样。 */
function valueInput(rule: AlertRule, value: number): string {
  if (isRate(rule)) return String(value / 100);
  return rule === "balance_low" ? formatMoneyInput(value) : String(value);
}

/** 百分数文本转万分比，最多两位小数、0 到 100。 */
function parsePercent(text: string): number | null {
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const bps = Math.round(Number(text) * 100);
  return bps <= 10000 ? bps : null;
}

/** 按规则解析数值，不合法时返回错误文案。 */
function parseValue(d: AlertThresholdDraft, rule: AlertRule): { value?: number; error?: string } {
  if (d.mode === "off") return { value: OFF_VALUE[rule] };
  if (isRate(rule)) {
    const bps = parsePercent(d.value.trim());
    return bps === null ? { error: "0 到 100 的百分数，最多两位小数。" } : { value: bps };
  }
  if (isCount(rule)) {
    const text = d.value.trim();
    const n = Number(text);
    return /^\d+$/.test(text) && Number.isSafeInteger(n) && n > 0 ? { value: n } : { error: rule === "channel_queue_wait" ? "大于 0 的整数秒。" : "大于 0 的整数次数。" };
  }
  const micro = parseMoney(d.value);
  if (micro === null) return { error: "金额，最多 6 位小数。" };
  return !d.owner_id && micro !== 0 ? { error: "全局默认只能是 0：各账号币种不同，要设金额请按账号单独设。" } : { value: micro };
}

/** 校验并换算；新建时按规则只带它能用的作用对象。 */
export function alertThresholdInput(d: AlertThresholdDraft): { body?: AlertThresholdInput; errors: Errors<AlertThresholdDraft> } {
  const errors: Errors<AlertThresholdDraft> = {};
  if (!d.rule) return { errors: { rule: "选择规则。" } };
  const meta = ALERT_RULES[d.rule];
  if (meta.operator && d.mcc && !validMcc(d.mcc)) errors.mcc = "三位数字，例如 510；留空表示不按国家区分。";
  if (meta.operator && d.mnc && (!d.mcc || !validMnc(d.mnc))) errors.mnc = d.mcc ? "两到三位数字，留空表示全部运营商。" : "先填 MCC。";
  if (d.mode === "baseline" && !meta.baseline) errors.mode = "只有送达率能和自身基线比较。";
  const v = parseValue(d, d.rule);
  if (v.error) errors.value = v.error;
  if (Object.keys(errors).length) return { errors };
  const owner = d.owner_id ? Number(d.owner_id) : null;
  return {
    errors,
    body: {
      rule: d.rule, channel_id: meta.owner === "channel" ? owner : null, account_id: meta.owner === "account" ? owner : null,
      mcc: meta.operator && d.mcc ? d.mcc : null, mnc: meta.operator && d.mnc ? d.mnc : null, mode: d.mode, value: v.value!, note: d.note.trim() || null,
    },
  };
}

/** 作用对象的具体程度，越大越优先：通道或账号 4、MCC 2、MNC 1；全局是 0（docs/07 第 2.4 节）。 */
export function specificity(t: Pick<AlertThreshold, "channel_id" | "account_id" | "mcc" | "mnc">): number {
  return (t.channel_id !== null || t.account_id !== null ? 4 : 0) + (t.mcc ? 2 : 0) + (t.mnc ? 1 : 0);
}

/** 列表里的作用对象：全局默认，或“名称 · MCC-MNC”。 */
export function scopeText(t: AlertThreshold, ownerName: (t: AlertThreshold) => string): string {
  if (specificity(t) === 0) return "全局默认";
  const parts = [];
  if (t.channel_id !== null || t.account_id !== null) parts.push(ownerName(t));
  if (t.mcc) parts.push(t.mnc ? `${t.mcc}-${t.mnc}` : `${t.mcc} 全部运营商`);
  return parts.join(" · ");
}

/** 送达率、排队时长与连续失败的告警条件；不告警与余额告警线由页面用徽标、金额组件展示。 */
export function conditionText(t: AlertThreshold): string {
  if (t.rule === "channel_queue_wait") return `预计排队超过 ${t.value} 秒`;
  if (t.rule === "channel_consecutive_failures") return `连续失败 ${t.value} 次`;
  if (t.rule === "account_consecutive_failures") return `连续 ${t.value} 条没送达`;
  return t.mode === "baseline" ? `低于自身 7 天均值的 ${t.value / 100}%` : `低于 ${t.value / 100}%`;
}

/** 列表按作用对象的类型筛选：全局默认、按通道、按账号、只按国家或运营商（不指定通道与账号）。 */
export type ScopeKind = "global" | "channel" | "account" | "operator";

export const SCOPE_OPTIONS: { value: ScopeKind | ""; label: string }[] = [
  { value: "", label: "全部对象" }, { value: "global", label: "全局默认" }, { value: "channel", label: "按通道" },
  { value: "account", label: "按账号" }, { value: "operator", label: "只按国家或运营商" },
];

export function scopeKind(t: Pick<AlertThreshold, "channel_id" | "account_id" | "mcc">): ScopeKind {
  if (t.channel_id !== null) return "channel";
  if (t.account_id !== null) return "account";
  return t.mcc ? "operator" : "global";
}

/** 搜索用的文本：作用对象（通道名、账号名与客户名）、#ID、MCC-MNC 与备注，小写。 */
export function searchText(t: AlertThreshold, ownerName: (t: AlertThreshold) => string): string {
  const owner = t.channel_id ?? t.account_id;
  return [scopeText(t, ownerName), owner !== null ? `#${owner}` : "", t.mcc && t.mnc ? `${t.mcc}-${t.mnc}` : t.mcc ?? "", t.note ?? ""].join(" ").toLowerCase();
}
